/**
 * Codice Fiscale italiano — parser + validator client-side.
 * Struttura CF (16 char):
 *   3 CGN  (cognome consonanti + eventuali vocali)
 *   3 NOM  (nome consonanti + eventuali vocali)
 *   2 YY   (anno nascita)
 *   1 MM   (mese: A=gen, B=feb, C=mar, D=apr, E=mag, H=giu, L=lug, M=ago, P=set, R=ott, S=nov, T=dic)
 *   2 DD   (giorno: se donna +40)
 *   4 LOC  (codice catastale comune/stato, "Z" = estero)
 *   1 CHK  (checksum letter)
 *
 * Espone: parseCodiceFiscale(cf) → { valid, dataNascita, sesso, codiceCatastale }
 * Non fa lookup del comune (richiede tabella; il parser resta stateless).
 */

const MESI = { A:0, B:1, C:2, D:3, E:4, H:5, L:6, M:7, P:8, R:9, S:10, T:11 };

// checksum table (odd + even chars, IT standard)
const ODD_MAP = {
  "0":1, "1":0, "2":5, "3":7, "4":9, "5":13, "6":15, "7":17, "8":19, "9":21,
  A:1, B:0, C:5, D:7, E:9, F:13, G:15, H:17, I:19, J:21, K:2, L:4, M:18,
  N:20, O:11, P:3, Q:6, R:8, S:12, T:14, U:16, V:10, W:22, X:25, Y:24, Z:23,
};
const EVEN_MAP = {
  "0":0, "1":1, "2":2, "3":3, "4":4, "5":5, "6":6, "7":7, "8":8, "9":9,
  A:0, B:1, C:2, D:3, E:4, F:5, G:6, H:7, I:8, J:9, K:10, L:11, M:12,
  N:13, O:14, P:15, Q:16, R:17, S:18, T:19, U:20, V:21, W:22, X:23, Y:24, Z:25,
};
const CHECK_LETTER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function validateChecksum(cf) {
  if (cf.length !== 16) return false;
  const body = cf.slice(0, 15).toUpperCase();
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const c = body[i];
    // posizione 1-indexed: dispari = odd, pari = even
    sum += (i % 2 === 0) ? ODD_MAP[c] : EVEN_MAP[c];
    if (sum < 0 || isNaN(sum)) return false;
  }
  return CHECK_LETTER[sum % 26] === cf[15].toUpperCase();
}

/**
 * Parse un CF italiano.
 * Ritorna: { valid: boolean, dataNascita: "yyyy-mm-dd"|null, sesso: "M"|"F"|null, codiceCatastale: string|null, error?: string }
 */
export function parseCodiceFiscale(cfRaw) {
  const cf = (cfRaw || "").toUpperCase().replace(/\s/g, "");
  if (!/^[A-Z0-9]{16}$/.test(cf)) {
    return { valid: false, dataNascita: null, sesso: null, codiceCatastale: null,
             error: "Formato non valido (servono 16 caratteri alfanumerici)" };
  }
  if (!validateChecksum(cf)) {
    return { valid: false, dataNascita: null, sesso: null, codiceCatastale: null,
             error: "Checksum non valido — verifica il CF" };
  }
  const yy = cf.slice(6, 8);
  const mm_letter = cf[8];
  const dd = parseInt(cf.slice(9, 11), 10);
  const loc = cf.slice(11, 15);
  if (!(mm_letter in MESI)) {
    return { valid: false, dataNascita: null, sesso: null, codiceCatastale: null,
             error: "Codice mese non valido" };
  }
  const isFemale = dd > 40;
  const day = isFemale ? dd - 40 : dd;
  const year2 = parseInt(yy, 10);
  // heuristica anno: se yy > 30 → 19xx, altrimenti 20xx (2026 minus 24 = 2002)
  const nowYY = new Date().getFullYear() % 100;
  const year = year2 <= nowYY ? 2000 + year2 : 1900 + year2;
  const month = MESI[mm_letter];
  const dt = new Date(Date.UTC(year, month, day));
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month || dt.getUTCDate() !== day) {
    return { valid: false, dataNascita: null, sesso: null, codiceCatastale: null,
             error: "Data di nascita implicita non valida" };
  }
  const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return {
    valid: true,
    dataNascita: iso,
    sesso: isFemale ? "F" : "M",
    codiceCatastale: loc,  // Es. F839 = Napoli. Lookup comune → server-side o tabella
  };
}

export default parseCodiceFiscale;
